import { useMutation, gql } from "@apollo/client"
import moment from "moment"
import { v4 as uuid } from "uuid"
import { useMakeOptimistic } from ".."
import { toId } from "../../utils"
import serializationKeys from "../serializationKeys"
import { JOB_FIELDS, JOB_STEP_FIELDS } from "./useQueryJobs"

const JOB_STEP_ADD_NOTE_MUTATION = gql`
  ${JOB_STEP_FIELDS}
  mutation JobStepAddNote($job: ID!, $step: ID!, $text: String!) {
    job(id: $job) {
      step(id: $step) {
        addNote(text: $text) {
          ...JobStepFields
        }
      }
    }
  }
`

const JOB_STEP_DELETE_NOTE_MUTATION = gql`
  ${JOB_STEP_FIELDS}
  mutation JobStepDeleteNote($job: ID!, $step: ID!, $note: ID!) {
    job(id: $job) {
      step(id: $step) {
        note(id: $note) {
          delete {
            ...JobStepFields
          }
        }
      }
    }
  }
`

const JOB_STEP_COMPLETE_MUTATION = gql`
  ${JOB_FIELDS}
  mutation JobStepComplete(
    $job: ID!
    $step: ID!
    $complete: Boolean!
    $skip: Boolean
    $response: String
    $responseUploads: [ID!]
  ) {
    job(id: $job) {
      step(id: $step) {
        complete(complete: $complete, skip: $skip, response: $response, responseUploads: $responseUploads) {
          ...JobFields
        }
      }
    }
  }
`

const makeJobStepAddNoteOptimisticResponse = (variables, author) => {
  const { step, text } = variables

  const result = {
    __typename: "Mutation",
    job: {
      step: {
        addNote: {
          ...JSON.parse(JSON.stringify(step)),
        },
      },
    },
  }

  result.job.step.addNote.notes.push({
    id: uuid(),
    text,
    author,
    createdAt: moment().format(),
  })

  return result
}

const makeJobStepCompleteOptimisticResponse = (variables, completedBy) => {
  const { job, step, complete, skip, response, responseUploads } = variables

  const result = {
    __typename: "Mutation",
    job: {
      step: {
        complete: {
          ...JSON.parse(JSON.stringify(job)),
        },
      },
    },
  }

  const resultJob = result.job.step.complete

  const stepIndex = resultJob.status.steps.findIndex((item) => toId(item) === toId(step))
  if (stepIndex > -1) {
    resultJob.status.steps[stepIndex] = {
      ...resultJob.status.steps[stepIndex],
      response,
      responseUploads: responseUploads || [],
      skip: skip || false,
      completedAt: complete || skip ? moment().format() : null,
      completedBy,
    }
  }

  const steps = resultJob.status.steps
  const percentComplete = Math.floor((steps.filter((item) => item.completedAt).length / steps.length) * 100)

  resultJob.percentComplete = percentComplete
  resultJob.status.completed = percentComplete === 100
  resultJob.status.completedAt = percentComplete === 100 ? moment().format() : null

  return result
}

const useMakeJobStepOptimistic = () => {
  const { currentUser } = useMakeOptimistic()

  return {
    makeJobStepCompleteOptimisticResponse: (variables) =>
      makeJobStepCompleteOptimisticResponse(variables, currentUser()),
    makeJobStepAddNoteOptimisticResponse: (variables) => makeJobStepAddNoteOptimisticResponse(variables, currentUser()),
  }
}

const useMutationJobStepAddNote = (job) =>
  useMutation(JOB_STEP_ADD_NOTE_MUTATION, {
    context: {
      serializationKey: serializationKeys.job(job),
    },
  })

const useMutationJobStepDeleteNote = (job) =>
  useMutation(JOB_STEP_DELETE_NOTE_MUTATION, {
    context: {
      serializationKey: serializationKeys.job(job),
    },
    update(cache, { errors, ...rest }, { variables: { note } }) {
      if (errors) {
        return
      }
      const normalizedId = cache.identify({ id: toId(note), __typename: "JobProcessStepNote" })
      cache.evict({ id: normalizedId })
      cache.gc()
    },
  })

const useMutationJobStepComplete = (job) =>
  useMutation(JOB_STEP_COMPLETE_MUTATION, {
    context: {
      serializationKey: serializationKeys.job(job),
    },
  })

export { useMutationJobStepAddNote, useMutationJobStepDeleteNote, useMutationJobStepComplete, useMakeJobStepOptimistic }
